import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import styled, { css, keyframes } from 'styled-components';
import { ChevronLeft, ChevronRight, MessageSquare, Users, FileText, Image, LayoutGrid, Puzzle, Mic, AppWindow, Newspaper, X, Monitor, type LucideIcon } from 'lucide-react';

import { useTranslation } from 'react-i18next';
import { buildGoogleLoginUrl } from '@/api/client';
import { API_BASE_URL } from '@/api/authFetch';
import { isDesktop } from '@/lib/platform';
import { openExternalUrl } from '@/lib/tauriBridge';
import type { AppLocale } from '@/i18n/resolveAppLocale';
import { CookieBanner } from '@/components/common/CookieBanner';

const loadAuth3DGalleryCanvas = () => import('./Auth3DGalleryCanvas');
const Auth3DGalleryCanvas = lazy(() =>
  loadAuth3DGalleryCanvas().then((m) => ({ default: m.Auth3DGalleryCanvas })),
);

/* ═══════════════════════════════════════════
   Animations
   ═══════════════════════════════════════════ */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-60px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const slideFromRight = keyframes`
  from { opacity: 0; transform: translateX(60px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const slideFromBottom = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slidePanelEnterFromRight = keyframes`
  from { transform: translate3d(100%, 0, 0); opacity: 1; }
  to   { transform: translate3d(0, 0, 0); opacity: 1; }
`;

const slidePanelEnterFromLeft = keyframes`
  from { transform: translate3d(-100%, 0, 0); opacity: 1; }
  to   { transform: translate3d(0, 0, 0); opacity: 1; }
`;

const slidePanelExitToLeft = keyframes`
  from { transform: translate3d(0, 0, 0); opacity: 1; }
  to   { transform: translate3d(-100%, 0, 0); opacity: 1; }
`;

const slidePanelExitToRight = keyframes`
  from { transform: translate3d(0, 0, 0); opacity: 1; }
  to   { transform: translate3d(100%, 0, 0); opacity: 1; }
`;

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const twinkle = keyframes`
  0%, 100% { opacity: 0.15; transform: scale(0.8); }
  50%      { opacity: 1;    transform: scale(1.2); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50%      { opacity: 1; }
`;

const featureHoverWobble = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg); }
  20%      { transform: translate3d(-1px, 0, 0) rotate(-0.35deg); }
  45%      { transform: translate3d(1.5px, -1px, 0) rotate(0.4deg); }
  70%      { transform: translate3d(-1px, 1px, 0) rotate(-0.2deg); }
`;

const dockBounce = keyframes`
  0%   { transform: translateY(0) rotateX(0); }
  25%  { transform: translateY(-18px) rotateX(-6deg); }
  50%  { transform: translateY(-4px) rotateX(-2deg); }
  75%  { transform: translateY(-10px) rotateX(-4deg); }
  100% { transform: translateY(0) rotateX(0); }
`;

const tooltipIn = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(4px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`;


const reboDockCue = keyframes`
  0%   { transform: translateY(0) scale(1); }
  22%  { transform: translateY(-12px) scale(1.06); }
  45%  { transform: translateY(0) scale(1); }
  66%  { transform: translateY(-7px) scale(1.03); }
  100% { transform: translateY(0) scale(1); }
`;

const reboPreviewCycle = keyframes`
  0%   { opacity: 0; transform: translateX(-50%) translateY(12px) scale(0.88); }
  14%  { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
  84%  { opacity: 1; transform: translateX(-50%) translateY(-6px) scale(1.02); }
  100% { opacity: 0; transform: translateX(-50%) translateY(-18px) scale(0.94); }
`;

const reboPreviewFloat = keyframes`
  0%   { transform: translateY(0px) rotate(-2deg); }
  50%  { transform: translateY(-8px) rotate(2deg); }
  100% { transform: translateY(0px) rotate(-2deg); }
`;

const logoCuteBounce = keyframes`
  0%   { transform: translateY(0) scale(1) rotate(0deg); }
  12%  { transform: translateY(-12px) scale(1.05) rotate(-6deg); }
  26%  { transform: translateY(-2px) scale(1.02) rotate(5deg); }
  42%  { transform: translateY(-10px) scale(1.06) rotate(-4deg); }
  58%  { transform: translateY(0) scale(1.03) rotate(3deg); }
  74%  { transform: translateY(-5px) scale(1.04) rotate(-2deg); }
  100% { transform: translateY(0) scale(1) rotate(0deg); }
`;

const logoSparkLeft = keyframes`
  0%   { opacity: 0; transform: translate(0, 0) scale(0.45); }
  30%  { opacity: 1; transform: translate(-10px, -8px) scale(1); }
  100% { opacity: 0; transform: translate(-24px, -22px) scale(1.08); }
`;

const logoSparkRight = keyframes`
  0%   { opacity: 0; transform: translate(0, 0) scale(0.45); }
  30%  { opacity: 1; transform: translate(10px, -9px) scale(1); }
  100% { opacity: 0; transform: translate(24px, -24px) scale(1.04); }
`;

/* ═══════════════════════════════════════════
   Shared
   ═══════════════════════════════════════════ */


type LandingSlide = 0 | 1 | 2;
type SlideTransitionDirection = 'forward' | 'backward';
type SlidePanelState = 'active' | 'enter-right' | 'enter-left' | 'exit-left' | 'exit-right';
type ReboSequencePhase = 'idle' | 'bounce' | 'preview';
type FeatureFocusKey = 'connectivity' | 'creativity' | 'efficiency';
type FeatureKeywordState = 'active' | 'next' | 'prev';

interface LandingDockItem {
  id: 'agent' | 'prev' | 'next' | 'rebo';
  labelKey: string;
  icon?: LucideIcon;
  iconSrc?: string;
  iconContain?: boolean;
}

const LANDING_DOCK_ICON_SIZE = 40;
const LANDING_DOCK_GAP = 10;
const LANDING_DOCK_PAD_X = 12;
const LANDING_DOCK_PAD_Y = 6;
const LANDING_DOCK_RADIUS = '22.37%';
const LANDING_DOCK_LIFT_Y = -10;
const LANDING_DOCK_TILT_DEG = 8;
const LANDING_DOCK_NEIGHBOR_TILT_DEG = 4;
const LANDING_DOCK_GLOW_RADIUS = 88;
const LANDING_DOCK_ICON_INNER = Math.round(LANDING_DOCK_ICON_SIZE * 0.52);
const LANDING_SLIDE_COUNT = 3;
const LANDING_SLIDE_TRANSITION_MS = 720;
const LANDING_WHEEL_NAV_LOCK_MS = LANDING_SLIDE_TRANSITION_MS + 120;
const LANDING_WHEEL_DELTA_THRESHOLD = 18;
const FEATURE_FOCUS_CYCLE_MS = 2400;
const REBO_SEQUENCE_CUE_MS = 220;
const REBO_SEQUENCE_TOTAL_MS = 2960;
const REBO_SEQUENCE_RESTART_DELAY_MS = 16;
const LANDING_MEDIA_VERSION = '20260327-landing-media-1';
const HERO_LANDING_BG_SRC = `${import.meta.env.BASE_URL}assets/landing_ep01_home.webp?v=${LANDING_MEDIA_VERSION}`;
const LANDING_AGENT_LOGO_SRC = `${import.meta.env.BASE_URL}assets/agent_logo.webp?v=${LANDING_MEDIA_VERSION}`;
const LANDING_REBO_ICON_SRC = `${import.meta.env.BASE_URL}assets/helmet-48.webp?v=${LANDING_MEDIA_VERSION}`;
const SHARED_SHOWCASE_BG_OVERLAY = 'rgba(0, 0, 0, 0.34)';
const REBO_PREVIEW_IMAGE_SRC = '/assets/rebo.webp';

const LANDING_DOCK_ITEMS: LandingDockItem[] = [
  {
    id: 'agent',
    labelKey: 'auth:dock.agent',
    iconSrc: LANDING_AGENT_LOGO_SRC,
    iconContain: true,
  },
  {
    id: 'prev',
    labelKey: 'common:button.previous',
    icon: ChevronLeft,
  },
  {
    id: 'next',
    labelKey: 'common:button.next',
    icon: ChevronRight,
  },
  {
    id: 'rebo',
    labelKey: 'auth:dock.rebo',
    iconSrc: LANDING_REBO_ICON_SRC,
    iconContain: true,
  },
];

function clampSlideIndex(index: number): LandingSlide {
  return Math.max(0, Math.min(index, LANDING_SLIDE_COUNT - 1)) as LandingSlide;
}

const PageRoot = styled.div`
  width: 100%;
  height: 100vh;
  overflow-x: hidden;
  overflow-y: hidden;

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
`;

const SectionTitle = styled.h2`
  font-size: calc(1.75rem + 1px);
  font-weight: 700;
  text-align: center;
  letter-spacing: -0.02em;
  line-height: 1.3;
  margin: 0 0 10px;
  color: #fff;
`;

const SectionSub = styled.p`
  font-size: calc(0.9rem + 1px);
  text-align: center;
  line-height: 1.6;
  margin: 0 0 40px;
  color: rgba(255, 255, 255, 0.66);
`;

const Star = styled.div<{ $x: number; $y: number; $size: number; $delay: number; $duration: number; $paused?: boolean }>`
  position: absolute;
  left: ${p => p.$x}%;
  top: ${p => p.$y}%;
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 0 ${p => p.$size * 2}px ${p => p.$size * 0.5}px rgba(255, 255, 255, 0.4);
  animation: ${twinkle} ${p => p.$duration}s ${p => p.$delay}s ease-in-out infinite;
  animation-play-state: ${p => (p.$paused ? 'paused' : 'running')};
  will-change: transform, opacity;
`;

const revealCss = css<{ $visible?: boolean }>`
  opacity: ${p => (p.$visible ? 1 : 0)};
  transform: translateY(${p => (p.$visible ? 0 : 40)}px);
  transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
`;

const ShowcaseSection = styled.section`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: url("${HERO_LANDING_BG_SRC}") no-repeat center center;
  background-size: cover;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    background: ${SHARED_SHOWCASE_BG_OVERLAY};
    pointer-events: none;
  }
`;

const SlideViewport = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  z-index: 1;
  backface-visibility: hidden;
  transform: translateZ(0);
`;

const SlideRail = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  isolation: isolate;
`;

const SlidePanel = styled.div<{ $state: SlidePanelState }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  contain: layout paint style;
  backface-visibility: hidden;
  transform: translateZ(0);
  will-change: transform;
  pointer-events: ${({ $state }) => ($state === 'exit-left' || $state === 'exit-right' ? 'none' : 'auto')};
  z-index: ${({ $state }) => ($state === 'active' ? 2 : $state.startsWith('enter') ? 3 : 1)};

  ${({ $state }) => $state === 'active' && css`
    transform: translate3d(0, 0, 0);
  `}

  ${({ $state }) => $state === 'enter-right' && css`
    animation: ${slidePanelEnterFromRight} 720ms cubic-bezier(0.22, 1, 0.36, 1) both;
  `}

  ${({ $state }) => $state === 'enter-left' && css`
    animation: ${slidePanelEnterFromLeft} 720ms cubic-bezier(0.22, 1, 0.36, 1) both;
  `}

  ${({ $state }) => $state === 'exit-left' && css`
    animation: ${slidePanelExitToLeft} 720ms cubic-bezier(0.22, 1, 0.36, 1) both;
  `}

  ${({ $state }) => $state === 'exit-right' && css`
    animation: ${slidePanelExitToRight} 720ms cubic-bezier(0.22, 1, 0.36, 1) both;
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: translate3d(0, 0, 0);
  }
`;

const DockWrapper = styled.div`
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  perspective: 800px;
`;

const DockBar = styled.div`
  display: flex;
  align-items: flex-end;
  padding: ${LANDING_DOCK_PAD_Y}px ${LANDING_DOCK_PAD_X}px;
  gap: ${LANDING_DOCK_GAP}px;
  border-radius: 18px;
  position: relative;
  overflow: visible;

  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border: none;
  box-shadow: none;

  &::before {
    content: none;
  }
`;

const DockItemButton = styled.button<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 0;
  border: none;
  background: none;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

const DockItemVisual = styled.div<{
  $bouncing: boolean;
  $cue?: boolean;
  $hovered: boolean;
  $neighborDir: -1 | 0 | 1;
  $disabled?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  transform-style: preserve-3d;
  will-change: transform;
  transition: transform 0.35s cubic-bezier(0.25, 1, 0.5, 1);

  transform: ${({ $hovered, $neighborDir, $bouncing, $cue, $disabled }) => {
    if ($disabled) return 'translateY(0) rotateX(0)';
    if ($cue) return 'none';
    if ($bouncing) return 'none';
    if ($hovered) return `translateY(${LANDING_DOCK_LIFT_Y}px) rotateX(-${LANDING_DOCK_TILT_DEG}deg) scale(1.02)`;
    if ($neighborDir !== 0) {
      return `translateY(${LANDING_DOCK_LIFT_Y * 0.25}px) rotateY(${$neighborDir * LANDING_DOCK_NEIGHBOR_TILT_DEG}deg)`;
    }
    return 'translateY(0) rotateX(0)';
  }};

  ${({ $cue, $disabled }) =>
    $cue &&
    !$disabled &&
    css`
      animation: ${reboDockCue} 820ms cubic-bezier(0.22, 1, 0.36, 1);
    `}

  ${({ $bouncing, $cue, $disabled }) =>
    $bouncing &&
    !$cue &&
    !$disabled &&
    css`
      animation: ${dockBounce} 0.5s cubic-bezier(0.28, 0.84, 0.42, 1);
    `}
`;

const IconSquare = styled.div<{ $hovered: boolean; $disabled?: boolean }>`
  width: ${LANDING_DOCK_ICON_SIZE}px;
  height: ${LANDING_DOCK_ICON_SIZE}px;
  border-radius: ${LANDING_DOCK_RADIUS};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;

  color: ${({ $hovered, $disabled }) =>
    $disabled
      ? 'rgba(255, 255, 255, 0.32)'
      : $hovered
        ? 'rgba(255, 255, 255, 1)'
        : 'rgba(255, 255, 255, 0.82)'};
  transition: color 0.24s ease,
    box-shadow 0.24s ease,
    background-color 0.24s ease,
    opacity 0.24s ease;
  opacity: ${({ $disabled }) => ($disabled ? 0.35 : 1)};
  background: ${({ $hovered }) => ($hovered ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.03)')};
  box-shadow: ${({ $hovered }) =>
    $hovered ? '0 0 18px rgba(255, 255, 255, 0.14)' : 'none'};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(
      158deg,
      rgba(255, 255, 255, 0.08) 0%,
      rgba(255, 255, 255, 0.02) 32%,
      transparent 52%
    );
    pointer-events: none;
    opacity: ${({ $hovered }) => ($hovered ? 1 : 0.38)};
    transition: opacity 0.24s ease;
  }
`;

const IconImg = styled.img<{ $contain?: boolean; $hovered?: boolean; $disabled?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $contain }) => ($contain ? 'contain' : 'cover')};
  border-radius: inherit;
  padding: ${({ $contain }) => ($contain ? '5px' : '0')};
  opacity: ${({ $hovered, $disabled }) => ($disabled ? 0.32 : $hovered ? 1 : 0.9)};
  transition: opacity 0.24s ease, filter 0.24s ease;
  filter: ${({ $hovered }) =>
    ($hovered ? 'brightness(1.12) saturate(1.04) contrast(1.05)' : 'brightness(1.08) saturate(1.02) contrast(1.05)')};
`;

const TooltipBubble = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: calc(100% + 24px);
  left: 50%;
  transform: translateX(-50%);
  padding: 5px 12px;
  border-radius: 6px;
  font-size: calc(0.75rem + 1px);
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? '#1c1c1e' : '#18181b'};
  color: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.14)'};
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
  text-rendering: geometricPrecision;
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
  animation: ${tooltipIn} 0.15s ease-out;
`;

const ReboDockPreview = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: 50%;
  bottom: calc(100% + 40px);
  transform: translateX(-50%);
  width: 116px;
  height: 116px;
  z-index: 12;
  pointer-events: none;
  opacity: 0;

  @media (max-width: 640px) {
    width: 100px;
    height: 100px;
    bottom: calc(100% + 32px);
  }

  ${({ $visible }) =>
    $visible &&
    css`
      animation: ${reboPreviewCycle} 3200ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
    `}
`;

const ReboDockPreviewBubble = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${reboPreviewFloat} 1.8s ease-in-out infinite;
  will-change: transform;
`;

const ReboDockPreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  will-change: transform, opacity;
  backface-visibility: hidden;
  transform: translateZ(0);
  filter:
    brightness(1.07)
    saturate(1.12)
    contrast(1.1)
    drop-shadow(0 12px 22px rgba(0, 0, 0, 0.2))
    drop-shadow(0 2px 10px rgba(255, 255, 255, 0.08));
`;

const DockReflection = styled.div`
  width: 0;
  height: 0;
  margin-top: 0;
  border-radius: 2px;
  position: relative;
  overflow: hidden;
  background: transparent;

  &::after {
    content: none;
  }
`;

const BottomLegalWrap = styled.div`
  position: absolute;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  z-index: 12;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  pointer-events: none;

  @media (max-width: 768px) {
    bottom: 12px;
    gap: 12px;
  }
`;

const LegalBar = styled.div`
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0;
`;

const LegalLink = styled.a`
  font-size: calc(0.78rem + 1px);
  color: rgba(255, 255, 255, 0.68);
  text-decoration: none;
  letter-spacing: 0.02em;
  transition: color 220ms ease;

  &:hover {
    color: rgba(255, 255, 255, 0.96);
  }
`;

const LegalText = styled.span`
  font-size: calc(0.78rem + 1px);
  color: rgba(255, 255, 255, 0.42);
  letter-spacing: 0.02em;
`;

const LegalDivider = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
`;

/* ═══════════════════════════════════════════
   Section 1 — Hero
   ═══════════════════════════════════════════ */
const HeroSection = styled.section`
  flex: 0 0 100vw;
  width: 100vw;
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 24px 40px;
  position: relative;
  overflow: hidden;
  contain: layout paint style;
  backface-visibility: hidden;
  transform: translateZ(0);

  @media (max-width: 768px) {
    padding: 24px 20px 32px;
  }
`;

const HeroStars = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 75%;
  height: 33.333%;
  z-index: 1;
  pointer-events: none;
  contain: paint;
  mask-image: linear-gradient(to right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.92) 82%, rgba(0, 0, 0, 0) 100%);
  -webkit-mask-image: linear-gradient(to right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.92) 82%, rgba(0, 0, 0, 0) 100%);
`;

const HeroContent = styled.div<{ $active?: boolean }>`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: ${p => (p.$active ? 1 : 0.4)};
  transform: translateY(${p => (p.$active ? 0 : 28)}px) scale(${p => (p.$active ? 1 : 0.98)});
  transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
  animation: ${fadeIn} 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

const LogoMotionWrap = styled.div<{ $animate: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  will-change: transform;

  ${({ $animate }) => $animate && css`
    animation: ${logoCuteBounce} 900ms cubic-bezier(0.22, 1, 0.36, 1);

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 18px;
      width: 12px;
      height: 12px;
      border-radius: 999px;
      pointer-events: none;
      background: radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(249,115,22,0.9) 58%, rgba(249,115,22,0) 100%);
      filter: blur(0.2px);
    }

    &::before {
      left: -4px;
      animation: ${logoSparkLeft} 720ms 80ms ease-out both;
    }

    &::after {
      right: -4px;
      animation: ${logoSparkRight} 720ms 120ms ease-out both;
    }
  `}
`;

const LogoWrap = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 28px;
  overflow: hidden;
  margin-bottom: 24px;
  @media (max-width: 480px) { width: 96px; height: 96px; border-radius: 22px; }
`;

const LogoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  margin: 0;
`;

const HeroTitle = styled.h1`
  margin: 0 0 10px;
  font-size: calc(2.4rem + 1px);
  font-weight: 800;
  color: #F97316;
  text-align: center;
  letter-spacing: -0.03em;
  text-shadow: 0 0 40px rgba(249, 115, 22, 0.3);
`;

const HeroDesc = styled.p`
  margin: 0 0 6px;
  font-size: calc(1rem + 1px);
  font-weight: 400;
  color: rgba(255, 255, 255, 0.66);
  text-align: center;
  letter-spacing: 0.04em;
`;

const GoogleButton = styled.button`
  width: 320px;
  max-width: 100%;
  height: 56px;
  border-radius: 14px;
  border: 1px solid rgba(249, 115, 22, 0.2);
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(24px) saturate(1.4);
  -webkit-backdrop-filter: blur(24px) saturate(1.4);
  color: #fff;
  font-size: calc(1.06rem + 1px);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 250ms cubic-bezier(0.22, 1, 0.36, 1);
  position: relative;
  overflow: hidden;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.06) inset,
    0 4px 20px rgba(0, 0, 0, 0.15);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 14px;
    padding: 1px;
    background: linear-gradient(135deg, rgba(249,115,22,0.3), transparent 50%, rgba(249,115,22,0.15));
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.08), transparent);
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 200ms ease;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(249, 115, 22, 0.35);
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.08) inset,
      0 0 30px rgba(249, 115, 22, 0.12),
      0 8px 32px rgba(0, 0, 0, 0.25);
    transform: translateY(-2px);
    &::after { opacity: 1; animation: ${shimmer} 1.5s infinite; }
  }

  &:active { transform: translateY(0); }

  svg { flex-shrink: 0; }
`;

const DownloadTrigger = styled.div`
  position: fixed;
  top: 20px;
  right: 24px;
  z-index: 100;
`;

const DownloadBtn = styled.button`
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(20px) saturate(1.3);
  -webkit-backdrop-filter: blur(20px) saturate(1.3);
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 180ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.22);
    color: #fff;
  }

  svg { width: 15px; height: 15px; flex-shrink: 0; }
`;

const DownloadDropdown = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  width: 100%;
  padding: 16px 6px 6px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(30, 30, 32, 0.92);
  backdrop-filter: blur(24px) saturate(1.6);
  -webkit-backdrop-filter: blur(24px) saturate(1.6);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  opacity: ${p => (p.$open ? 1 : 0)};
  transform: translateY(${p => (p.$open ? 0 : -6)}px);
  pointer-events: ${p => (p.$open ? 'auto' : 'none')};
  transition: opacity 150ms ease, transform 150ms ease;
`;

const DownloadItem = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  transition: background 120ms ease;

  &:hover { background: rgba(255, 255, 255, 0.08); }

  svg { width: 18px; height: 18px; flex-shrink: 0; opacity: 0.9; }
`;

const DownloadItemText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const DownloadItemName = styled.span`
  font-size: 0.84rem;
  font-weight: 500;
`;

const DownloadItemSub = styled.span`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 400;
`;

const AppleLogo = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const DownloadHint = styled.div`
  padding: 6px 10px 2px;
  font-size: 0.65rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.3);

  code {
    background: rgba(255, 255, 255, 0.08);
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 0.6rem;
    color: rgba(255, 255, 255, 0.5);
  }
`;

const TypingText = styled.p`
  margin: 0 0 32px;
  font-size: calc(0.84rem + 1px);
  font-weight: 500;
  color: rgba(255, 255, 255, 0.66);
  text-align: center;
  letter-spacing: 0.08em;
  min-height: 1.4em;
  word-spacing: 0.15em;
`;

/* ═══════════════════════════════════════════
   Section 3 — Features
   ═══════════════════════════════════════════ */
const FeaturesSection = styled.section`
  flex: 0 0 100vw;
  width: 100vw;
  height: 100%;
  min-height: 100%;
  padding: 72px 24px 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  overflow: hidden;
  contain: layout paint style;
  backface-visibility: hidden;
  transform: translateZ(0);

  @media (max-width: 768px) {
    padding: 60px 20px 120px;
  }
`;

const FeatStars = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 60%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
  contain: paint;
`;

const FeaturesInner = styled.div<{ $visible?: boolean }>`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${revealCss}
`;

const slideAnimMap = {
  left: slideFromLeft,
  right: slideFromRight,
  bottom: slideFromBottom,
};

const BentoGrid = styled.div<{ $visible?: boolean }>`
  position: relative;
  z-index: 2;
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: auto;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 480px;
  }
`;

const FeatureCardMotion = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  transform-origin: center center;
`;

const FeatureCard = styled.div<{
  $span?: string;
  $dir?: SlideDir;
  $delay?: number;
  $visible?: boolean;
  $highlighted?: boolean;
  $square?: boolean;
  $interactive?: boolean;
}>`
  grid-column: ${p => p.$span || 'span 1'};
  background: ${({ $highlighted }) =>
    $highlighted
      ? 'linear-gradient(180deg, rgba(255, 245, 236, 0.16) 0%, rgba(255, 255, 255, 0.1) 100%)'
      : 'linear-gradient(180deg, rgba(255, 255, 255, 0.035) 0%, rgba(255, 255, 255, 0.018) 100%)'};
  border: 1px solid ${({ $highlighted }) => ($highlighted ? 'rgba(249, 115, 22, 0.2)' : 'rgba(255, 255, 255, 0.06)')};
  border-radius: 16px;
  padding: 28px 24px;
  position: relative;
  overflow: hidden;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  aspect-ratio: ${({ $square }) => ($square ? '1 / 1' : 'auto')};
  min-height: ${({ $square }) => ($square ? '224px' : 'auto')};
  transform-origin: center center;
  transition:
    background 300ms ease,
    border-color 300ms ease,
    box-shadow 300ms ease,
    filter 300ms ease,
    transform 300ms ease;
  box-shadow: ${({ $highlighted }) =>
    $highlighted
      ? '0 12px 24px rgba(249, 115, 22, 0.05), 0 8px 18px rgba(0, 0, 0, 0.06), inset 0 0 0 1px rgba(255, 255, 255, 0.04)'
      : '0 4px 10px rgba(0, 0, 0, 0.025), inset 0 0 0 1px rgba(255, 255, 255, 0.018)'};
  filter: ${({ $highlighted }) => ($highlighted ? 'brightness(1.03)' : 'brightness(1)')};
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);

  opacity: 0;
  ${p => p.$visible && p.$dir && css`
    animation: ${slideAnimMap[p.$dir]} 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${p.$delay ?? 0}s both;
  `}

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 16px;
    padding: 1px;
    background: ${({ $highlighted }) =>
      $highlighted
        ? 'linear-gradient(145deg, rgba(255,255,255,0.2), transparent 40%, transparent 60%, rgba(255,255,255,0.08))'
        : 'linear-gradient(145deg, rgba(255,255,255,0.08), transparent 40%, transparent 60%, rgba(255,255,255,0.03))'};
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  &:hover {
    background: ${({ $highlighted }) =>
      $highlighted
        ? 'linear-gradient(180deg, rgba(255, 245, 236, 0.2) 0%, rgba(255, 255, 255, 0.12) 100%)'
        : 'linear-gradient(180deg, rgba(255, 255, 255, 0.055) 0%, rgba(255, 255, 255, 0.03) 100%)'};
    border-color: ${({ $highlighted }) => ($highlighted ? 'rgba(249, 115, 22, 0.24)' : 'rgba(249, 115, 22, 0.1)')};
    transform: translateY(-3px) scale(1.01);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover ${FeatureCardMotion} {
      animation: ${featureHoverWobble} 820ms ease-in-out infinite;
    }
  }

  @media (max-width: 768px) {
    grid-column: span 1 !important;
    aspect-ratio: auto;
    min-height: auto;
  }
`;

const FeatureIcon = styled.div<{ $highlighted?: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 13px;
  background: ${({ $highlighted }) =>
    $highlighted
      ? 'linear-gradient(145deg, rgba(249,115,22,0.42), rgba(249,115,22,0.22))'
      : 'linear-gradient(145deg, rgba(249,115,22,0.22), rgba(249,115,22,0.1))'};
  color: ${({ $highlighted }) => ($highlighted ? '#FFF1E2' : '#FFB067')};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
  box-shadow:
    ${({ $highlighted }) =>
      $highlighted
        ? '0 4px 16px rgba(249, 115, 22, 0.28), 0 0 0 1px rgba(249, 115, 22, 0.2) inset'
        : '0 2px 10px rgba(249, 115, 22, 0.16), 0 0 0 1px rgba(249, 115, 22, 0.12) inset'};
`;

const FeatureName = styled.h3<{ $highlighted?: boolean }>`
  font-size: calc(1rem + 1px);
  font-weight: 600;
  color: ${({ $highlighted }) => ($highlighted ? '#fff' : 'rgba(255, 255, 255, 0.96)')};
  margin: 0 0 8px;
  text-shadow: ${({ $highlighted }) => ($highlighted ? '0 0 18px rgba(249, 115, 22, 0.22)' : 'none')};
`;

const FeatureDesc = styled.p<{ $highlighted?: boolean; $compact?: boolean }>`
  font-size: calc(0.825rem + 1px);
  color: ${({ $highlighted }) => ($highlighted ? 'rgba(255, 255, 255, 0.82)' : 'rgba(255, 255, 255, 0.62)')};
  line-height: 1.65;
  margin: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ $compact }) => ($compact ? 5 : 'unset')};
  overflow: hidden;
`;

const AiImageSheetLayer = styled.div<{ $open: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 6;
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
`;

const AiImageSheetBackdrop = styled.button<{ $open: boolean }>`
  position: absolute;
  inset: 0;
  border: none;
  background: rgba(255, 255, 255, 0.02);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity 420ms cubic-bezier(0.22, 1, 0.36, 1);
  cursor: default;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
`;

const AiImageSheetStack = styled.div<{ $open: boolean }>`
  position: absolute;
  left: 50%;
  bottom: 0;
  width: min(calc(100% - 28px), 1080px);
  height: 60%;
  transform: translate3d(-50%, ${({ $open }) => ($open ? '0' : '104%')}, 0);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  filter: ${({ $open }) => ($open ? 'blur(0)' : 'blur(14px)')};
  transition:
    transform 680ms cubic-bezier(0.16, 1, 0.3, 1),
    opacity 460ms ease,
    filter 460ms ease;
  pointer-events: none;
  will-change: transform, opacity, filter;

  @media (max-width: 768px) {
    width: calc(100% - 12px);
    height: 60%;
  }
`;

const AiImageSheet = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.038);
  border-radius: 30px 30px 0 0;
  border: 1px solid rgba(255, 255, 255, 0.14);
  overflow: hidden;
  box-shadow:
    0 16px 48px rgba(0, 0, 0, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(30px) saturate(145%);
  -webkit-backdrop-filter: blur(30px) saturate(145%);

  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
  }
`;

const AiImageSheetHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 28px 18px;
  background: transparent;

  @media (max-width: 768px) {
    padding: 20px 18px 16px;
  }
`;

const AiImageSheetHeaderText = styled.div`
  min-width: 0;
`;

const AiImageSheetTitle = styled.h3`
  margin: 0 0 8px;
  color: #fff;
  font-size: clamp(calc(1.3rem + 1px), calc(2.2vw + 1px), calc(1.8rem + 1px));
  font-weight: 800;
  letter-spacing: -0.03em;
`;

const AiImageSheetSub = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: calc(0.9rem + 1px);
  line-height: 1.7;
  max-width: 760px;
`;

const AiImageSheetClose = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.74);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 200ms ease, color 200ms ease, border-color 200ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    border-color: rgba(255, 255, 255, 0.22);
  }
`;

const AiImageSheetBody = styled.div`
  flex: 1;
  min-height: 0;
  padding: 0;
  background: transparent;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const AiImageSheetCanvas = styled(Auth3DGalleryCanvas)`
  width: 100%;
  height: 100%;
  border-radius: 0;
  border: none;
  background: transparent;
  box-shadow: none;
`;

const AiImageSheetCanvasFallback = styled.div`
  width: 100%;
  height: 100%;
`;

const FeaturesKeywordCallout = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 0 0 20px;
  color: rgba(255, 255, 255, 0.5);
  font-size: calc(0.76rem + 1px);
  letter-spacing: 0.08em;
  text-transform: none;

  &::before {
    content: '';
    width: 46px;
    height: 1px;
    background: linear-gradient(90deg, rgba(249, 115, 22, 0), rgba(249, 115, 22, 0.72));
  }

  &::after {
    content: '';
    width: 46px;
    height: 1px;
    background: linear-gradient(90deg, rgba(249, 115, 22, 0.72), rgba(249, 115, 22, 0));
  }

  @media (max-width: 768px) {
    gap: 10px;
    margin-bottom: 16px;

    &::before,
    &::after {
      width: 34px;
    }
  }
`;

const FeaturesKeywordViewport = styled.div`
  position: relative;
  width: 230px;
  height: 82px;
  overflow: hidden;
  perspective: 1000px;

  @media (max-width: 768px) {
    width: 210px;
    height: 76px;
  }
`;

const FeaturesKeywordWord = styled.span<{ $state: FeatureKeywordState; $keyword: FeatureFocusKey }>`
  position: absolute;
  left: 50%;
  top: 50%;
  font-weight: 700;
  font-size: calc(1.06rem + 1px);
  line-height: 1;
  white-space: nowrap;
  transform-style: preserve-3d;
  transition:
    transform 760ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 760ms ease,
    filter 760ms ease,
    color 760ms ease,
    text-shadow 760ms ease;

  @media (max-width: 768px) {
    font-size: calc(0.98rem + 1px);
  }

  ${({ $state }) =>
    $state === 'active'
      ? css`
          transform: translate(-50%, -50%) rotateX(0deg) scale(1);
          opacity: 1;
          filter: blur(0);
          color: rgba(255, 255, 255, 0.96);
          text-shadow: 0 0 18px rgba(249, 115, 22, 0.3);
        `
      : $state === 'next'
        ? css`
            transform: translate(-50%, calc(-50% + 34px)) rotateX(-86deg) scale(0.92);
            opacity: 0.26;
            filter: blur(1.2px);
            color: rgba(255, 255, 255, 0.38);
            text-shadow: none;
          `
        : css`
            transform: translate(-50%, calc(-50% - 34px)) rotateX(86deg) scale(0.92);
            opacity: 0.26;
            filter: blur(1.2px);
            color: rgba(255, 255, 255, 0.38);
            text-shadow: none;
          `}
`;

/* ═══════════════════════════════════════════
   Section 5 — FAQ
   ═══════════════════════════════════════════ */
const FAQSection = styled.section`
  flex: 0 0 100vw;
  width: 100vw;
  height: 100%;
  min-height: 100%;
  padding: 174px 24px 140px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow-x: hidden;
  overflow-y: auto;
  contain: layout paint style;
  backface-visibility: hidden;
  transform: translateZ(0);

  & > * { position: relative; z-index: 1; }

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;

  @media (max-width: 768px) {
    padding: 156px 20px 120px;
  }
`;

const FAQList = styled.div<{ $visible?: boolean }>`
  max-width: 640px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  ${revealCss}
`;

const FAQItem = styled.div<{ $open: boolean }>`
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  overflow: hidden;

  &:first-child {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
`;

const FAQChevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  font-size: calc(1.1rem + 1px);
  font-weight: 300;
  color: ${p => (p.$open ? '#F97316' : 'rgba(255, 255, 255, 0.25)')};
  transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1),
    color 0.3s ease;
  transform: rotate(${p => (p.$open ? '90deg' : '0deg')});
  flex-shrink: 0;
  width: 20px;
  text-align: center;
`;

const FAQQuestion = styled.button<{ $open: boolean }>`
  width: 100%;
  padding: 20px 0;
  background: none;
  border: none;
  color: ${p => (p.$open ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.6)')};
  font-size: calc(0.925rem + 1px);
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 14px;
  transition: color 0.3s ease;
  letter-spacing: -0.01em;

  &:hover {
    color: rgba(255, 255, 255, 0.95);

    ${FAQChevron} {
      color: #F97316;
    }
  }
`;

const FAQAnswer = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${p => (p.$open ? '1fr' : '0fr')};
  opacity: ${p => (p.$open ? 1 : 0)};
  transition: grid-template-rows 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.35s ease;

  & > div {
    overflow: hidden;
  }
`;

const FAQAnswerInner = styled.div`
  padding: 0 0 22px 34px;

  p {
    margin: 0;
    font-size: calc(0.85rem + 1px);
    color: rgba(255, 255, 255, 0.4);
    line-height: 1.75;
  }
`;

/* ═══════════════════════════════════════════
   Google Logo
   ═══════════════════════════════════════════ */
const GoogleLogo: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M47.532 24.553c0-1.632-.132-3.272-.414-4.877H24.48v9.242h12.963a11.089 11.089 0 0 1-4.804 7.28l-.044.291 6.972 5.404.483.048C43.96 38.14 47.532 31.87 47.532 24.553Z" fill="#4285F4" />
    <path d="M24.48 48c6.48 0 11.93-2.148 15.9-5.84l-7.41-5.742c-2.088 1.404-4.776 2.22-8.49 2.22-6.456 0-11.94-4.356-13.896-10.356l-.276.023-7.248 5.612-.095.264C7.236 42.588 15.24 48 24.48 48Z" fill="#34A853" />
    <path d="M10.584 28.282A14.768 14.768 0 0 1 9.78 24c0-1.5.252-2.952.78-4.282l-.014-.31-7.335-5.698-.24.114A24.022 24.022 0 0 0 .48 24c0 3.876.924 7.536 2.496 10.188l7.608-5.906Z" fill="#FBBC05" />
    <path d="M24.48 9.36c4.464 0 7.476 1.932 9.192 3.54l6.696-6.54C36.384 2.46 30.96 0 24.48 0 15.24 0 7.236 5.412 2.976 13.812L10.56 19.72c1.98-5.988 7.464-10.36 13.92-10.36Z" fill="#EB4335" />
  </svg>
);

/* ═══════════════════════════════════════════
   Data
   ═══════════════════════════════════════════ */
const STAR_COUNT_HERO = 30;
const TYPING_TEXT = 'EP01. Hello world! Hello, Rebo.';
const TYPING_SPEED = 70;
const START_DELAY = 1200;


type SlideDir = 'left' | 'right' | 'bottom';
interface FeatureConfig {
  id: string;
  icon: LucideIcon;
  name: string;
  descKey: string;
  span: string;
  dir: SlideDir;
  delay: number;
  focus: FeatureFocusKey | null;
  square?: boolean;
}

const FEATURE_FOCUS_ORDER: FeatureFocusKey[] = [
  'connectivity',
  'creativity',
  'efficiency',
];

const FEATURES: FeatureConfig[] = [
  { id: 'ai-chat', icon: MessageSquare, name: 'AI Chat', descKey: 'auth:features.aiChat', span: 'span 2', dir: 'left', delay: 0, focus: 'efficiency' },
  { id: 'group-chat', icon: Users, name: 'Group Chat', descKey: 'auth:features.groupChat', span: 'span 2', dir: 'right', delay: 0.1, focus: 'connectivity' },
  { id: 'feed', icon: Newspaper, name: 'Feed', descKey: 'auth:features.feed', span: 'span 1', dir: 'left', delay: 0.15, focus: 'connectivity' },
  { id: 'ai-documents', icon: FileText, name: 'AI Documents', descKey: 'auth:features.aiDocuments', span: 'span 1', dir: 'bottom', delay: 0.2, focus: 'creativity' },
  { id: 'ai-image', icon: Image, name: 'AI Image', descKey: 'auth:features.aiImage', span: 'span 1', dir: 'bottom', delay: 0.25, focus: 'creativity' },
  { id: 'docs-slides', icon: LayoutGrid, name: 'Docs & Slides', descKey: 'auth:features.docsSlides', span: 'span 1', dir: 'bottom', delay: 0.3, focus: 'efficiency' },
  { id: 'widgets', icon: AppWindow, name: 'Widgets', descKey: 'auth:features.widgets', span: 'span 2', dir: 'left', delay: 0.2, focus: 'efficiency' },
  { id: 'plugins', icon: Puzzle, name: 'Plugins', descKey: 'auth:features.plugins', span: 'span 1', dir: 'bottom', delay: 0.35, focus: 'connectivity' },
  { id: 'ai-voice', icon: Mic, name: 'AI Voice', descKey: 'auth:features.aiVoice', span: 'span 1', dir: 'bottom', delay: 0.4, focus: 'efficiency' },
];

const AI_IMAGE_FEATURE = FEATURES.find((feature) => feature.id === 'ai-image')!;

const FAQ_KEYS = [
  { qKey: 'auth:faq.q1', aKey: 'auth:faq.a1' },
  { qKey: 'auth:faq.q2', aKey: 'auth:faq.a2' },
  { qKey: 'auth:faq.q3', aKey: 'auth:faq.a3' },
  { qKey: 'auth:faq.q4', aKey: 'auth:faq.a4' },
  { qKey: 'auth:faq.q5', aKey: 'auth:faq.a5' },
];

/* ═══════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════ */
function seededRandom(seed: number) {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

function getFeatureKeywordState(index: number, activeIndex: number): FeatureKeywordState {
  const diff = (index - activeIndex + FEATURE_FOCUS_ORDER.length) % FEATURE_FOCUS_ORDER.length;
  if (diff === 0) return 'active';
  if (diff === 1) return 'next';
  return 'prev';
}

/* ═══════════════════════════════════════════
   Component
   ═══════════════════════════════════════════ */
export const AuthPage: React.FC = () => {
  const { t, i18n } = useTranslation(['auth', 'common']);
  const [typed, setTyped] = useState('');
  const [activeSlide, setActiveSlide] = useState<LandingSlide>(0);
  const [slideTransition, setSlideTransition] = useState<{
    from: LandingSlide;
    to: LandingSlide;
    direction: SlideTransitionDirection;
  } | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [agentLogoBurstId, setAgentLogoBurstId] = useState(0);
  const [aiImageSheetOpen, setAiImageSheetOpen] = useState(false);
  const [featureFocusIndex, setFeatureFocusIndex] = useState(0);
  const [reboSequencePhase, setReboSequencePhase] = useState<ReboSequencePhase>('idle');
  const [dockHoveredIdx, setDockHoveredIdx] = useState<number | null>(null);
  const [dockBouncingId, setDockBouncingId] = useState<LandingDockItem['id'] | null>(null);
  const showcaseRef = useRef<HTMLElement>(null);
  const faqSectionRef = useRef<HTMLElement>(null);
  const dockBarRef = useRef<HTMLDivElement>(null);
  const agentLogoBurstTimerRef = useRef<number | null>(null);
  const slideTransitionTimerRef = useRef<number | null>(null);
  const reboSequenceTimerRefs = useRef<number[]>([]);
  const wheelLockUntilRef = useRef(0);

  const visualActiveSlide = clampSlideIndex(slideTransition?.to ?? activeSlide);
  const isHeroActive = visualActiveSlide === 0;
  const isFeaturesActive = visualActiveSlide === 1;
  const isFaqActive = visualActiveSlide === 2;
  const isSlideTransitioning = slideTransition !== null;
  const activeFeatureFocus = FEATURE_FOCUS_ORDER[featureFocusIndex];

  const clearReboSequenceTimers = useCallback(() => {
    reboSequenceTimerRefs.current.forEach(timer => window.clearTimeout(timer));
    reboSequenceTimerRefs.current = [];
  }, []);

  useEffect(() => {
    let intervalId: number | null = null;
    const timeout = window.setTimeout(() => {
      let i = 0;
      intervalId = window.setInterval(() => {
        i++;
        setTyped(TYPING_TEXT.slice(0, i));
        if (i >= TYPING_TEXT.length && intervalId !== null) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
      }, TYPING_SPEED);
    }, START_DELAY);
    return () => {
      window.clearTimeout(timeout);
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
    };
  }, []);

  useEffect(() => {
    const preloadWorkspace = () => {
      void import('@/components/layout/Layout');
    };

    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(preloadWorkspace, { timeout: 1200 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timer = window.setTimeout(preloadWorkspace, 300);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isFeaturesActive) {
      setFeatureFocusIndex(0);
      return;
    }

    if (aiImageSheetOpen) {
      setFeatureFocusIndex(FEATURE_FOCUS_ORDER.indexOf('creativity'));
      return;
    }

    const timer = window.setInterval(() => {
      setFeatureFocusIndex((index) => (index + 1) % FEATURE_FOCUS_ORDER.length);
    }, FEATURE_FOCUS_CYCLE_MS);

    return () => window.clearInterval(timer);
  }, [aiImageSheetOpen, isFeaturesActive]);

  useEffect(() => {
    if (!isFeaturesActive && aiImageSheetOpen) {
      setAiImageSheetOpen(false);
    }
  }, [aiImageSheetOpen, isFeaturesActive]);

  useEffect(() => {
    if (!aiImageSheetOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAiImageSheetOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [aiImageSheetOpen]);

  useEffect(() => {
    if (!isFeaturesActive || isSlideTransitioning || aiImageSheetOpen) return;

    const preloadTimer = window.setTimeout(() => {
      void loadAuth3DGalleryCanvas();
    }, 140);

    return () => window.clearTimeout(preloadTimer);
  }, [aiImageSheetOpen, isFeaturesActive, isSlideTransitioning]);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const handleGoogleLogin = useCallback(() => {
    if (isDesktop) {
      const webOrigin = API_BASE_URL.includes('api-dev')
        ? 'https://dev.askyour.ai'
        : 'https://askyour.ai';
      const callback = `${webOrigin}/auth/callback?desktop=1`;
      const url = buildGoogleLoginUrl(callback);
      void openExternalUrl(url);
      return;
    }
    const callback = `${window.location.origin}/auth/callback`;
    window.location.href = buildGoogleLoginUrl(callback);
  }, []);

  const openAiImageSheet = useCallback(() => {
    setFeatureFocusIndex(FEATURE_FOCUS_ORDER.indexOf('creativity'));
    setAiImageSheetOpen(true);
  }, []);

  const closeAiImageSheet = useCallback(() => {
    setAiImageSheetOpen(false);
  }, []);

  const triggerAgentLogoBurst = useCallback((delayMs = 0) => {
    if (agentLogoBurstTimerRef.current !== null) {
      window.clearTimeout(agentLogoBurstTimerRef.current);
      agentLogoBurstTimerRef.current = null;
    }

    const runAnimation = () => {
      setAgentLogoBurstId(prev => prev + 1);
      agentLogoBurstTimerRef.current = null;
    };

    if (delayMs > 0) {
      agentLogoBurstTimerRef.current = window.setTimeout(runAnimation, delayMs);
      return;
    }

    runAnimation();
  }, []);

  const triggerReboSequence = useCallback((delayMs = 0) => {
    const runSequence = () => {
      setReboSequencePhase('idle');
      const restartTimer = window.setTimeout(() => {
        setReboSequencePhase('bounce');

        const previewTimer = window.setTimeout(() => {
          setReboSequencePhase('preview');
        }, REBO_SEQUENCE_CUE_MS);

        const finishTimer = window.setTimeout(() => {
          setReboSequencePhase('idle');
        }, REBO_SEQUENCE_TOTAL_MS);

        reboSequenceTimerRefs.current.push(previewTimer, finishTimer);
      }, REBO_SEQUENCE_RESTART_DELAY_MS);

      reboSequenceTimerRefs.current.push(restartTimer);
    };

    clearReboSequenceTimers();

    if (delayMs > 0) {
      const delayTimer = window.setTimeout(runSequence, delayMs);
      reboSequenceTimerRefs.current.push(delayTimer);
      return;
    }

    runSequence();
  }, [clearReboSequenceTimers]);

  const ensureShowcaseVisible = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (window.scrollY > window.innerHeight * 0.25) {
      showcaseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const goToSlide = useCallback((index: number) => {
    const nextSlide = clampSlideIndex(index);
    if (nextSlide === visualActiveSlide) {
      ensureShowcaseVisible();
      return;
    }
    if (isSlideTransitioning) return;

    const direction: SlideTransitionDirection = nextSlide > activeSlide ? 'forward' : 'backward';
    setSlideTransition({ from: activeSlide, to: nextSlide, direction });
    ensureShowcaseVisible();
    if (slideTransitionTimerRef.current !== null) {
      window.clearTimeout(slideTransitionTimerRef.current);
    }
    slideTransitionTimerRef.current = window.setTimeout(() => {
      setActiveSlide(nextSlide);
      setSlideTransition(null);
      slideTransitionTimerRef.current = null;
    }, LANDING_SLIDE_TRANSITION_MS);
  }, [activeSlide, ensureShowcaseVisible, isSlideTransitioning, visualActiveSlide]);

  const stepSlide = useCallback((direction: -1 | 1) => {
    if (isSlideTransitioning) return;
    goToSlide(activeSlide + direction);
  }, [activeSlide, goToSlide, isSlideTransitioning]);

  const getDockNeighborDir = useCallback((idx: number): -1 | 0 | 1 => {
    if (dockHoveredIdx === null) return 0;
    if (idx === dockHoveredIdx - 1) return -1;
    if (idx === dockHoveredIdx + 1) return 1;
    return 0;
  }, [dockHoveredIdx]);

  const handleDockMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const el = dockBarRef.current;
    if (el) {
      el.style.setProperty('--dock-mx', `${x}px`);
      el.style.setProperty('--dock-glow-opacity', '1');
    }
  }, []);

  const handleDockMouseLeave = useCallback(() => {
    setDockHoveredIdx(null);
    const el = dockBarRef.current;
    if (el) {
      el.style.setProperty('--dock-glow-opacity', '0');
    }
  }, []);

  const handleDockAction = useCallback((item: LandingDockItem) => {
    const isPrevDisabled = item.id === 'prev' && visualActiveSlide === 0;
    const isNextDisabled = item.id === 'next' && visualActiveSlide === LANDING_SLIDE_COUNT - 1;

    if (isPrevDisabled || isNextDisabled || isSlideTransitioning) return;

    setDockBouncingId(item.id);
    window.setTimeout(() => setDockBouncingId(null), 500);

    switch (item.id) {
      case 'agent':
        triggerAgentLogoBurst();
        break;
      case 'prev':
        stepSlide(-1);
        break;
      case 'next':
        stepSlide(1);
        break;
      case 'rebo':
        triggerReboSequence();
        break;
      default:
        break;
    }
  }, [isSlideTransitioning, stepSlide, triggerAgentLogoBurst, triggerReboSequence]);

  const handleSlideWheel = useCallback((event: React.WheelEvent<HTMLElement>) => {
    if (aiImageSheetOpen) return;
    if (Math.abs(event.deltaY) < LANDING_WHEEL_DELTA_THRESHOLD) return;
    if (Math.abs(event.deltaY) < Math.abs(event.deltaX)) return;

    const direction: -1 | 1 = event.deltaY > 0 ? -1 : 1;
    const faqEl = faqSectionRef.current;
    const isWithinFaq = isFaqActive && faqEl?.contains(event.target as Node);

    if (isWithinFaq && faqEl) {
      const atTop = faqEl.scrollTop <= 2;
      const atBottom = faqEl.scrollTop + faqEl.clientHeight >= faqEl.scrollHeight - 2;

      if ((direction > 0 && !atBottom) || (direction < 0 && !atTop)) {
        return;
      }
    }

    event.preventDefault();

    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (isSlideTransitioning || now < wheelLockUntilRef.current) return;

    const canNavigate = direction > 0
      ? visualActiveSlide < LANDING_SLIDE_COUNT - 1
      : visualActiveSlide > 0;
    if (!canNavigate) return;

    wheelLockUntilRef.current = now + LANDING_WHEEL_NAV_LOCK_MS;
    stepSlide(direction);
  }, [aiImageSheetOpen, isFaqActive, isSlideTransitioning, stepSlide, visualActiveSlide]);

  useEffect(() => () => {
    if (agentLogoBurstTimerRef.current !== null) {
      window.clearTimeout(agentLogoBurstTimerRef.current);
    }
    if (slideTransitionTimerRef.current !== null) {
      window.clearTimeout(slideTransitionTimerRef.current);
    }
    clearReboSequenceTimers();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName;
      if (
        target
        && (
          target.isContentEditable
          || tagName === 'INPUT'
          || tagName === 'TEXTAREA'
          || tagName === 'SELECT'
        )
      ) {
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        stepSlide(1);
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        stepSlide(-1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [stepSlide]);

  const heroStars = useMemo(
    () => Array.from({ length: STAR_COUNT_HERO }, (_, i) => ({
      id: i,
      x: seededRandom(i + 50) * 100,
      y: seededRandom(i + 100) * 100,
      size: 1 + seededRandom(i + 150) * 2.4,
      delay: seededRandom(i + 200) * 5,
      duration: 2.2 + seededRandom(i + 250) * 3.6,
    })),
    [],
  );

  const featStars = useMemo(
    () => Array.from({ length: 25 }, (_, i) => ({
      id: i,
      x: seededRandom(i + 1500) * 100,
      y: seededRandom(i + 1600) * 100,
      size: 1 + seededRandom(i + 1700) * 2,
      delay: seededRandom(i + 1800) * 5,
      duration: 2 + seededRandom(i + 1900) * 4,
    })),
    [],
  );

  const renderedSlides = slideTransition
    ? [clampSlideIndex(slideTransition.from), clampSlideIndex(slideTransition.to)]
    : [clampSlideIndex(activeSlide)];

  const getSlidePanelState = (slide: LandingSlide): SlidePanelState => {
    if (!slideTransition) return 'active';
    if (slide === slideTransition.from) {
      return slideTransition.direction === 'forward' ? 'exit-left' : 'exit-right';
    }
    return slideTransition.direction === 'forward' ? 'enter-right' : 'enter-left';
  };

  const renderHeroSlide = () => (
    <HeroSection>
      <HeroStars>
        {heroStars.map(s => (
          <Star
            key={`hs-${s.id}`}
            $x={s.x}
            $y={s.y}
            $size={s.size}
            $delay={s.delay}
            $duration={s.duration}
            $paused={!isHeroActive}
          />
        ))}
      </HeroStars>

      <HeroContent $active={isHeroActive}>
        <LogoMotionWrap
          key={`agent-logo-burst-${agentLogoBurstId}`}
          $animate={agentLogoBurstId > 0}
        >
          <LogoWrap>
            <LogoImg src={LANDING_AGENT_LOGO_SRC} alt="Logo" />
          </LogoWrap>
        </LogoMotionWrap>
        <HeroTitle>askyour.ai</HeroTitle>
        <HeroDesc>{t('auth:login.heroDesc')}</HeroDesc>
        <TypingText>{typed}</TypingText>

        <GoogleButton data-track="auth_google_login" onClick={handleGoogleLogin}>
          <GoogleLogo />
          {t('auth:login.googleButton')}
        </GoogleButton>

        <DockWrapper style={{ marginTop: 28 }}>
          <DockBar
            ref={dockBarRef}
            onMouseMove={handleDockMouseMove}
            onMouseLeave={handleDockMouseLeave}
          >
            {LANDING_DOCK_ITEMS.map((item, idx) => {
              const isHovered = dockHoveredIdx === idx;
              const neighborDir = getDockNeighborDir(idx);
              const isDisabled = isSlideTransitioning
                || (item.id === 'prev' && visualActiveSlide === 0)
                || (item.id === 'next' && visualActiveSlide === LANDING_SLIDE_COUNT - 1);
              const isReboCueActive = item.id === 'rebo' && reboSequencePhase === 'bounce';
              const isReboPreviewVisible = item.id === 'rebo' && reboSequencePhase === 'preview';

              return (
                <DockItemButton
                  key={item.id}
                  type="button"
                  aria-label={t(item.labelKey)}
                  $disabled={isDisabled}
                  onMouseEnter={() => !isDisabled && setDockHoveredIdx(idx)}
                  onMouseLeave={() => !isDisabled && setDockHoveredIdx(null)}
                  onClick={() => !isDisabled && handleDockAction(item)}
                >
                  <TooltipBubble $visible={isHovered}>
                    {t(item.labelKey)}
                  </TooltipBubble>
                  {isReboPreviewVisible && (
                    <ReboDockPreview $visible>
                      <ReboDockPreviewBubble>
                        <ReboDockPreviewImg src={REBO_PREVIEW_IMAGE_SRC} alt="Rebo" draggable={false} />
                      </ReboDockPreviewBubble>
                    </ReboDockPreview>
                  )}
                  <DockItemVisual
                    $bouncing={dockBouncingId === item.id}
                    $cue={isReboCueActive}
                    $hovered={isHovered}
                    $neighborDir={neighborDir}
                    $disabled={isDisabled}
                  >
                    <IconSquare $hovered={isHovered} $disabled={isDisabled}>
                      {item.iconSrc ? (
                        <IconImg
                          src={item.iconSrc}
                          alt={t(item.labelKey)}
                          draggable={false}
                          $contain={item.iconContain}
                          $hovered={isHovered}
                          $disabled={isDisabled}
                        />
                      ) : item.icon ? (
                        <item.icon
                          size={LANDING_DOCK_ICON_INNER}
                          strokeWidth={2}
                        />
                      ) : null}
                    </IconSquare>
                  </DockItemVisual>
                </DockItemButton>
              );
            })}
          </DockBar>
          <DockReflection />
        </DockWrapper>

      </HeroContent>
    </HeroSection>
  );

  const renderFeaturesSlide = () => (
    <FeaturesSection>
      <FeatStars>
        {featStars.map(s => (
          <Star
            key={`fs-${s.id}`}
            $x={s.x}
            $y={s.y}
            $size={s.size}
            $delay={s.delay}
            $duration={s.duration}
            $paused={!isFeaturesActive}
          />
        ))}
      </FeatStars>

      <FeaturesInner $visible={isFeaturesActive}>
        <FeaturesKeywordCallout>
          <FeaturesKeywordViewport>
            {FEATURE_FOCUS_ORDER.map((keyword, index) => (
              <FeaturesKeywordWord
                key={keyword}
                $keyword={keyword}
                $state={getFeatureKeywordState(index, featureFocusIndex)}
              >
                {keyword}
              </FeaturesKeywordWord>
            ))}
          </FeaturesKeywordViewport>
        </FeaturesKeywordCallout>
        <SectionTitle>{t('auth:login.featuresTitle')}</SectionTitle>
        <SectionSub>{t('auth:login.featuresSub')}</SectionSub>

        <BentoGrid>
          {FEATURES.map((f) => {
            const isHighlighted = isFeaturesActive && f.focus === activeFeatureFocus;
            const isAiImageCard = f.id === 'ai-image';

            return (
              <FeatureCard
                key={f.id}
                $span={f.span}
                $dir={f.dir}
                $delay={f.delay}
                $visible={isFeaturesActive}
                $highlighted={isHighlighted}
                $square={f.square}
                $interactive={isAiImageCard}
                role={isAiImageCard ? 'button' : undefined}
                tabIndex={isAiImageCard ? 0 : undefined}
                aria-haspopup={isAiImageCard ? 'dialog' : undefined}
                aria-expanded={isAiImageCard ? aiImageSheetOpen : undefined}
                onClick={isAiImageCard ? openAiImageSheet : undefined}
                onKeyDown={isAiImageCard ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openAiImageSheet();
                  }
                } : undefined}
              >
                <FeatureCardMotion>
                  <FeatureIcon $highlighted={isHighlighted}><f.icon size={22} /></FeatureIcon>
                  <FeatureName $highlighted={isHighlighted}>{f.name}</FeatureName>
                  <FeatureDesc $highlighted={isHighlighted} $compact={f.square}>{t(f.descKey)}</FeatureDesc>
                </FeatureCardMotion>
              </FeatureCard>
            );
          })}
        </BentoGrid>
      </FeaturesInner>

      <AiImageSheetLayer $open={aiImageSheetOpen}>
        <AiImageSheetBackdrop
          type="button"
          aria-label="Close AI image generation panel"
          onClick={closeAiImageSheet}
          $open={aiImageSheetOpen}
        />
        <AiImageSheetStack $open={aiImageSheetOpen}>
          <AiImageSheet role="dialog" aria-modal="true" aria-label={t('auth:login.galleryTitle')}>
            <AiImageSheetHeader>
              <AiImageSheetHeaderText>
                <AiImageSheetTitle>{t('auth:login.galleryTitle')}</AiImageSheetTitle>
                <AiImageSheetSub>{t(AI_IMAGE_FEATURE.descKey)}</AiImageSheetSub>
              </AiImageSheetHeaderText>
              <AiImageSheetClose
                type="button"
                aria-label="Close AI image generation panel"
                onClick={closeAiImageSheet}
              >
                <X size={18} />
              </AiImageSheetClose>
            </AiImageSheetHeader>
            <AiImageSheetBody>
              {aiImageSheetOpen ? (
                <Suspense fallback={<AiImageSheetCanvasFallback />}>
                  <AiImageSheetCanvas active={isFeaturesActive && aiImageSheetOpen} />
                </Suspense>
              ) : null}
            </AiImageSheetBody>
          </AiImageSheet>
        </AiImageSheetStack>
      </AiImageSheetLayer>
    </FeaturesSection>
  );

  const renderFaqSlide = () => (
    <FAQSection ref={faqSectionRef}>
      <SectionTitle>FAQ</SectionTitle>
      <SectionSub>{t('auth:login.faqSub')}</SectionSub>

      <FAQList $visible={isFaqActive}>
        {FAQ_KEYS.map((f, i) => (
          <FAQItem key={i} $open={openFaq === i}>
            <FAQQuestion $open={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
              <FAQChevron $open={openFaq === i}>&#x203A;</FAQChevron>
              {t(f.qKey)}
            </FAQQuestion>
            <FAQAnswer $open={openFaq === i}>
              <FAQAnswerInner>
                <p>{t(f.aKey)}</p>
              </FAQAnswerInner>
            </FAQAnswer>
          </FAQItem>
        ))}
      </FAQList>
    </FAQSection>
  );

  const renderSlideContent = (slide: LandingSlide) => {
    switch (slide) {
      case 0:
        return renderHeroSlide();
      case 1:
        return renderFeaturesSlide();
      case 2:
        return renderFaqSlide();
      default:
        return renderHeroSlide();
    }
  };

  const [downloadOpen, setDownloadOpen] = useState(false);
  const downloadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!downloadOpen) return;
    const close = (e: MouseEvent) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target as Node)) {
        setDownloadOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [downloadOpen]);

  return (
    <PageRoot>
      <Helmet>
        <link rel="preload" as="image" href={HERO_LANDING_BG_SRC} type="image/webp" />
      </Helmet>

      {!isDesktop && <DownloadTrigger
        ref={downloadRef}
        onMouseEnter={() => setDownloadOpen(true)}
        onMouseLeave={() => setDownloadOpen(false)}
      >
        <DownloadBtn
          data-track="auth_desktop_download_trigger"
          onClick={() => setDownloadOpen(prev => !prev)}
        >
          <Monitor />
          {t('auth:login.downloadDesktopApp', { defaultValue: 'Download Desktop App' })}
        </DownloadBtn>
        <DownloadDropdown $open={downloadOpen}>
          <DownloadItem
            href="/desktop/latest/askyour.ai-arm64.dmg"
            download
            data-track="auth_desktop_download_mac_arm64"
          >
            <AppleLogo />
            <DownloadItemText>
              <DownloadItemName>{t('auth:login.macName', { defaultValue: 'Mac' })}</DownloadItemName>
              <DownloadItemSub>Apple Silicon</DownloadItemSub>
            </DownloadItemText>
          </DownloadItem>
          <DownloadItem
            href="/desktop/latest/askyour.ai-x64.dmg"
            download
            data-track="auth_desktop_download_mac_x64"
          >
            <AppleLogo />
            <DownloadItemText>
              <DownloadItemName>{t('auth:login.macName', { defaultValue: 'Mac' })}</DownloadItemName>
              <DownloadItemSub>Intel</DownloadItemSub>
            </DownloadItemText>
          </DownloadItem>
        </DownloadDropdown>
      </DownloadTrigger>}

      <ShowcaseSection ref={showcaseRef} onWheel={handleSlideWheel}>
        <SlideViewport>
          <SlideRail>
            {renderedSlides.map(slide => (
              <SlidePanel
                key={`slide-${slide}`}
                $state={getSlidePanelState(slide)}
              >
                {renderSlideContent(slide)}
              </SlidePanel>
            ))}
          </SlideRail>
        </SlideViewport>

        {isHeroActive && !isSlideTransitioning && <CookieBanner />}
        <BottomLegalWrap>
          {!isHeroActive && (
            <DockWrapper>
              <DockBar
                ref={dockBarRef}
                onMouseMove={handleDockMouseMove}
                onMouseLeave={handleDockMouseLeave}
              >
                {LANDING_DOCK_ITEMS.map((item, idx) => {
                  const isHovered = dockHoveredIdx === idx;
                  const neighborDir = getDockNeighborDir(idx);
                  const isDisabled = isSlideTransitioning
                    || (item.id === 'prev' && (visualActiveSlide as number) === 0)
                    || (item.id === 'next' && visualActiveSlide === LANDING_SLIDE_COUNT - 1);
                  const isReboCueActive = item.id === 'rebo' && reboSequencePhase === 'bounce';
                  const isReboPreviewVisible = item.id === 'rebo' && reboSequencePhase === 'preview';

                  return (
                    <DockItemButton
                      key={item.id}
                      type="button"
                      aria-label={t(item.labelKey)}
                      $disabled={isDisabled}
                      onMouseEnter={() => !isDisabled && setDockHoveredIdx(idx)}
                      onMouseLeave={() => !isDisabled && setDockHoveredIdx(null)}
                      onClick={() => !isDisabled && handleDockAction(item)}
                    >
                      <TooltipBubble $visible={isHovered}>
                        {t(item.labelKey)}
                      </TooltipBubble>
                      {isReboPreviewVisible && (
                        <ReboDockPreview $visible>
                          <ReboDockPreviewBubble>
                            <ReboDockPreviewImg src={REBO_PREVIEW_IMAGE_SRC} alt="Rebo" draggable={false} />
                          </ReboDockPreviewBubble>
                        </ReboDockPreview>
                      )}
                      <DockItemVisual
                        $bouncing={dockBouncingId === item.id}
                        $cue={isReboCueActive}
                        $hovered={isHovered}
                        $neighborDir={neighborDir}
                        $disabled={isDisabled}
                      >
                        <IconSquare $hovered={isHovered} $disabled={isDisabled}>
                          {item.iconSrc ? (
                            <IconImg
                              src={item.iconSrc}
                              alt={t(item.labelKey)}
                              draggable={false}
                              $contain={item.iconContain}
                              $hovered={isHovered}
                              $disabled={isDisabled}
                            />
                          ) : item.icon ? (
                            <item.icon
                              size={LANDING_DOCK_ICON_INNER}
                              strokeWidth={2}
                            />
                          ) : null}
                        </IconSquare>
                      </DockItemVisual>
                    </DockItemButton>
                  );
                })}
              </DockBar>
              <DockReflection />
            </DockWrapper>
          )}
          <LegalBar>
            <LegalLink href="/terms">{t('auth:login.footerTerms')}</LegalLink>
            <LegalDivider />
            <LegalLink href="/privacy">{t('auth:login.footerPrivacy')}</LegalLink>
            <LegalDivider />
            <LegalText>&copy; {new Date().getFullYear()} TEAMREBOOTT, Inc.</LegalText>
          </LegalBar>
        </BottomLegalWrap>
      </ShowcaseSection>
    </PageRoot>
  );
};
